import { Observable } from 'rxjs';

import { ElementRef, Injectable } from '@angular/core';
import { FormControl } from '@angular/forms';

import * as stripe from '@stripe/stripe-js';

import { NzFormControlComponent } from 'ng-zorro-antd/form';

import { Select } from '@ngxs/store';

import { TranslateService } from '@ngx-translate/core';

import { COLORS } from '@app/configurations';
import { Customer } from '@app/core/models/customer.models';
import { CustomerState } from '@app/core/store/states/customer/customer.state';
import { PaymentMethodType } from '@app/core/store/states/customer/payment-methods/payment-methods-state.models';
import { getCurrentStage } from '@app/core/utilities/url.utilities';
import { lastValue } from '@app/shared/operators/last-value.operator';
import { environment } from '@env/environment';

const inkColor = COLORS.PRIMARY_TEXT;
const mutedColor = '#909bab';

const elementStyles: stripe.StripeElementStyle = {
  base: {
    color: inkColor,
    fontWeight: '400',
    fontFamily: `proxima-nova, sans-serif`,
    fontSize: '14px',
    lineHeight: '21px',
    fontSmoothing: 'antialiased',
    ':focus': {
      color: inkColor,
    },
    '::placeholder': {
      color: mutedColor,
    },
  },
  invalid: {
    color: inkColor,
    ':focus': {
      color: inkColor,
    },
    '::placeholder': {
      color: mutedColor,
    },
  },
};

const elementClasses: stripe.StripeElementClasses = {
  focus: 'focus',
  empty: 'empty',
  invalid: 'invalid',
};

@Injectable()
export class PaymentMethodsStripeService {
  private stripe?: stripe.Stripe;

  private elements?: stripe.StripeElements;

  private readonly loadStripe: Promise<void> = new Promise(async (resolve) => {
    this.stripe = await stripe.loadStripe(environment.app.stages[getCurrentStage()].stripeKey, {
      locale: this.ts.currentLang as 'en' | 'de' | 'zh',
    });
    this.elements = this.stripe.elements({
      fonts: [{ cssSrc: 'https://d2m8tbbygx8orx.cloudfront.net/adspert/fonts/proxima-nova/font-styles.css' }],
    });

    resolve();
  });

  @Select(CustomerState.customer)
  readonly customer$!: Observable<Customer>;

  constructor(private ts: TranslateService) {}

  async createElement(
    name: stripe.StripeElementType | any,
    input: ElementRef<HTMLElement>,
    formItem: ElementRef<HTMLElement>,
    formControl: NzFormControlComponent,
  ): Promise<void> {
    await this.loadStripe;

    const control = formControl.validateControl as FormControl;

    if (!this.elements.getElement(name)) {
      const options = this.getElementOptions(name);
      const element = this.elements.create(name, options);

      element.mount(input.nativeElement);
      element.on('change', (event: stripe.StripeElementChangeEvent) => {
        control.markAsDirty();

        if (event.empty) {
          control.setErrors({ required: true }, { emitEvent: true });
        } else if (event.error) {
          control.setErrors({ invalid: true }, { emitEvent: true });
        } else if (event.complete) {
          control.setErrors({}, { emitEvent: true });
          control.setValue('-');
        }
      });

      element.on('focus', () => formItem.nativeElement.classList.add('focus'));
      element.on('blur', () => formItem.nativeElement.classList.remove('focus'));
    }
  }

  async destroyElement(name: stripe.StripeElementType | any): Promise<void> {
    await this.loadStripe;

    const element = this.elements.getElement(name);

    if (element) {
      element.destroy();
    }
  }

  async submit(
    billingDetails: { name: string; email?: string },
    type: PaymentMethodType,
    secret: string,
    newSubscription?: boolean,
  ): Promise<{ setupIntent?: stripe.SetupIntent; error?: stripe.StripeError }> {
    await this.loadStripe;

    const details = lastValue(this.customer$);

    if (details) {
      const stripeBillingDetails = {
        email: details.email,
        ...billingDetails,
      };

      if (type === 'CARD') {
        return this.stripe.confirmCardSetup(secret, {
          payment_method: {
            card: this.elements.getElement('cardNumber'),
            billing_details: stripeBillingDetails,
          },
        });
      } else if (type === 'SEPA') {
        stripeBillingDetails['address'] = {
          city: details?.city,
          country: details?.countryCode,
          line1: details?.address1,
          line2: details?.address2,
          postal_code: details?.zip,
        };
        return this.stripe.confirmSepaDebitSetup(secret, {
          payment_method: {
            sepa_debit: this.elements.getElement('iban'),
            billing_details: stripeBillingDetails,
          },
        });
      } else if (type == 'ALIPAY') {
        const params = new URLSearchParams(newSubscription ? { new_subscription: 'true' } : {});
        const returnUrl = `${environment.app.stages[getCurrentStage()].appBaseURL}/payment-methods/${
          details.customerId
        }/alipay/return?${params.toString()}`;

        return this.stripe
          .confirmSetup({
            clientSecret: secret,
            confirmParams: {
              return_url: returnUrl,
              payment_method_data: {
                billing_details: stripeBillingDetails,
              },
            },
            redirect: 'always',
          })
          .then(() => false as any);
      }
    }
  }

  private getElementOptions(name: stripe.StripeElementType): stripe.StripeIbanElementOptions {
    let options: stripe.StripeIbanElementOptions = { style: elementStyles, classes: elementClasses };

    if (name === 'iban') {
      const customer = lastValue(this.customer$);

      options = {
        ...options,
        placeholderCountry: customer.countryCode || 'DE',
        supportedCountries: ['SEPA'],
      };
    }

    return options;
  }
}
